import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Grid2 } from '@mui/material';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { CustomDialog } from 'components/shared/Dialog/CustomDialog';
import { EditableConditionsList } from 'components/shared/EditableConditionsList';
import { buildApprovalFormValues } from '../../../lib';
import { ApprovalFormValues } from '../../../types';
import { ApprovalDetailsFields } from './ApprovalDetailsFields';

export const EditApprovalOverviewDialog: React.FC = () => {
  const { isOpen, data, handleClose } = useModal(ModalsKeys.EditApprovalOverviewDialog);
  const [conditions, setConditions] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const { control, reset, getValues } = useForm<ApprovalFormValues>({
    defaultValues: buildApprovalFormValues({} as any, undefined, 0),
  });

  useEffect(() => {
    if (isOpen && data) {
      const conditionsFromTerms = data.approvalTerms?.conditions?.map((c: any) =>
        typeof c === 'string' ? c : c.description
      );
      setConditions([
        ...(conditionsFromTerms?.length ? conditionsFromTerms : (data.creditSubmission.contingencies ?? [])),
      ]);
      reset(buildApprovalFormValues(data.creditSubmission, data.approvalTerms, data.docFeeTotal));
    }
  }, [isOpen, data, reset]);

  const handleAddCondition = useCallback(() => {
    setConditions(prev => [...prev, '']);
  }, []);

  const handleRemoveCondition = useCallback((index: number) => {
    setConditions(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleConditionChange = useCallback((index: number, value: string) => {
    setConditions(prev => prev.map((c, i) => (i === index ? value : c)));
  }, []);

  const handleSave = async () => {
    const filtered = conditions.filter(c => c.trim() !== '');
    const formValues = getValues();
    setIsSaving(true);
    try {
      await data?.onSave(formValues, filtered);
      handleClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  const actions = (
    <>
      <Button onClick={handleCancel} disabled={isSaving}>
        Cancel
      </Button>
      <Button variant="contained" onClick={handleSave} disabled={isSaving} loading={isSaving}>
        Save Changes
      </Button>
    </>
  );

  return (
    <CustomDialog
      isOpen={isOpen}
      onClose={handleCancel}
      label="Edit Approval Terms"
      actions={actions}
      maxWidth="md"
      fullWidth
    >
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <ApprovalDetailsFields control={control} parsedDecisionId={data?.parsedDecisionId ?? null} />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <EditableConditionsList
            conditions={conditions}
            onAdd={handleAddCondition}
            onRemove={handleRemoveCondition}
            onChange={handleConditionChange}
          />
        </Grid2>
      </Grid2>
    </CustomDialog>
  );
};
